/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/


import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import userIcon from "dashboard-template/assets/images/userIcon.png";
import { HeaderEnum, PersonInterface } from "medicalsystem-shared";
import Typography from "components/generals/Typography";
import { calculateAge } from "utils/generic.utils";
import Box from "components/generals/Box";
import useHttp from "hooks/http.hook";
import { useEffect, useState, useMemo } from "react";
import { CoreAPI_URLs } from "constants/http.const";
import { Stack, CircularProgress, Box as MuiBox } from "@mui/material";
import Avatar from "components/generals/Avatar";
import { useTranslation } from "react-i18next";
import { useAuthenticatedAsset } from "hooks/useAuthenticatedAsset.hook";

/**
 * Properties for the HeaderPersonInformation component.
 * 
 * @interface HeaderProps
 * @property {Partial<PersonInterface>} [personInformation] - Person data to display
 * @property {boolean} [progressNote] - Whether displayed in progress note context (hides photo)
 */
interface HeaderProps {
  personInformation?: Partial<PersonInterface>;
  progressNote?: boolean;
}

/**
 * Person information header card component.
 * Displays person demographics with profile photo.
 * 
 * Features:
 * - Profile photo with authenticated blob loading
 * - Loading indicator with progress for photo
 * - Fallback to default user icon
 * - Person name and contact information
 * - Date of birth and calculated age
 * - Formatted phone numbers
 * - Address details (street, city, state, ZIP)
 * - Responsive grid layout
 * - Conditional photo display (hidden in progress notes)
 * - Automatic person data refresh via API
 * 
 * Photo Loading:
 * - Uses useAuthenticatedAsset hook for secure blob access
 * - Shows CircularProgress during load
 * - Displays load progress percentage
 * - Falls back to default icon on error
 * 
 * Data Display:
 * - Name: Full name from person object
 * - Age: Calculated from date of birth
 * - DOB: Formatted as YYYY-MM-DD
 * - Phone: Primary phone number
 * - Address: Complete address with city, state, ZIP
 * 
 * Layout Modes:
 * - Standard: Shows photo + demographics
 * - Progress Note: Demographics only (progressNote=true)
 * 
 * @param {HeaderProps} props - Component properties
 * @param {Partial<PersonInterface>} props.personInformation - Person data
 * @param {boolean} props.progressNote - Hide photo if true
 * 
 * @returns {JSX.Element} Person information header card
 */
function HeaderPersonInformation({
  personInformation,
  progressNote,
}: Readonly<HeaderProps>): JSX.Element {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [personModel, setPersonModel] = useState<Partial<PersonInterface>>();
  const { t } = useTranslation();
  const sex = personModel?.sex === "MALE"? t("headerPersonInformation.male"):t("headerPersonInformation.female");
  // Use authenticated asset hook for profile photo
  const { blobUrl: photoUrl, isLoading: photoLoading, progress } = useAuthenticatedAsset(
    personInformation?.photo
  );

  const formattedDate = useMemo(() => personModel?.dob?.split("T")[0], [personModel?.dob]);
  const age = useMemo(() => calculateAge(personModel?.dob), [personModel?.dob]);

  useEffect(() => {
    setPersonModel(personInformation);
    if (personInformation) {
      sendRequest(CoreAPI_URLs.PERSONS_GENERAL_OPERATIONS, onGetPerson, "GET", undefined, [
        {
          header: HeaderEnum.RESOURCE_ID,
          value: personInformation.id,
        },
      ]);
    }
  }, [personInformation]);

  const onGetPerson = (person: PersonInterface) => {
    if (person) {
      setPersonModel(person);
    } else {
      setPersonModel(null);
    }
  };
  return (
    <Card id="profile">
      <Box p={2}>
        <Grid container spacing={3}>
          {!progressNote && (
            <Grid item>
              <Stack direction="row" spacing={2}>
                {photoLoading ? (
                  <MuiBox 
                    sx={{ 
                      width: 200, 
                      height: 200, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: '50%'
                    }}
                  >
                    <MuiBox sx={{ position: 'relative', display: 'inline-flex' }}>
                      <CircularProgress 
                        variant="determinate" 
                        value={progress}
                        size={60}
                        thickness={4}
                      />
                      <MuiBox
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: 'absolute',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" color="secondary">
                          {`${progress}%`}
                        </Typography>
                      </MuiBox>
                    </MuiBox>
                  </MuiBox>
                ) : (
                  <Avatar
                    src={photoUrl || userIcon}
                    alt={personInformation.photo?.orgName || "profile-image"}
                    sx={{ width: 200, height: 200 }}
                  />
                )}
              </Stack>
            </Grid>
          )}
          <Grid item>
            <Box height="100%" mt={0.5} lineHeight={1}>
              {personModel ? (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h5" fontWeight="medium">
                      {personModel.name} {personModel.lastname}
                    </Typography>
                  </Grid>
                  {personModel.patient && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text" fontWeight="medium">
                        {t("headerPersonInformation.accountNumber")}:{" "}
                        {personModel.patient.accountNumber}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="medium">
                      {t("headerPersonInformation.age")}: {age} {t("headerPersonInformation.years")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="medium">
                      {t("headerPersonInformation.sex")}: {sex}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="medium">
                      {t("headerPersonInformation.birthDate")}: {formattedDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="medium" sx={{display: "inline-block", maxWidth: "530px"}}>
                      {t("headerPersonInformation.address")}: {personModel.address},{" "}
                      {personModel.city}, {personModel.state}, {personModel.country},{" "}
                      {personModel.zip}
                    </Typography>
                  </Grid>
                </>
              ) : (
                <Typography variant="h5" fontWeight="medium">
                  {t("headerPersonInformation.noPersonInformation")}:
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
}
export default HeaderPersonInformation;
